/*
 * Fragrance Lord smart search overlay (2026-10-25; smarter names and spelling 2026-10-26).
 *
 * Opens full screen from the header search box (desktop) or the search icon (phone), and from "/" or Cmd/Ctrl+K.
 * Every keystroke is answered in the browser from one index of the catalogue (fl-panel-index on the Fragrance Lord
 * server, rebuilt from Shopify every 15 minutes): no round trip per key.
 *
 *   - spelling does not have to be right ("baccarat rouje", "vanila", "creeed");
 *   - brands by any name: "dior", "ysl", "mfk", "tf" go straight to the brand;
 *   - it reads requests: "fresh for summer under $100 for him" = accords + season + price ceiling + gender;
 *     "like Aventus" ranks by shared notes;
 *   - in-stock bottles first, samples after the full bottles (unless samples were asked for);
 *   - descriptions the index cannot settle ("smells like a campfire") go to the fragrance advisor, whose picks
 *     arrive a second later above the instant results. If the AI is down, the panel works without it.
 *
 * If this file or the index fails, the theme's own search box keeps working: clicks are only taken over once the
 * dialog exists and the panel is enabled (snippets/fl-smart-search.liquid).
 */
(() => {
  const CFG = Object.assign(
    {
      enabled: false,
      api: '',
      searchUrl: '/search',
      trending: ['Oud', 'Vanilla', 'Leather', 'Men', 'Women', 'Niche', 'Designer', 'Under $75', 'On sale', 'Travel sizes'],
      describe: ['warm amber for date night', 'clean citrus for the office under $120', 'a gift for her who loves rose'],
    },
    window.FL_SMART_SEARCH || {}
  );
  // The API answers on its own name; a second address is tried only if the first cannot be reached (DNS not set up yet).
  const APIS = [].concat(CFG.api || []).filter(Boolean);
  const useApi = (base) => { CFG.api = base; CFG.index = base + '/index.json'; CFG.meta = base + '/meta'; CFG.ai = CFG.askOff ? '' : base + '/ask'; };
  useApi(APIS[0] || '');
  const dialog = document.getElementById('fl-smart-search');
  if (!dialog || typeof dialog.showModal !== 'function' || !CFG.api) return;
  // Until CFG.enabled is true the panel is only for whoever opened the site once with ?fl_search=1
  // (and ?fl_search=0 switches that browser back to the old search).
  let previewing = false;
  try {
    if (/[?&]fl_search=1\b/.test(location.search)) localStorage.setItem('fl-search-preview', '1');
    if (/[?&]fl_search=0\b/.test(location.search)) localStorage.removeItem('fl-search-preview');
    previewing = localStorage.getItem('fl-search-preview') === '1';
  } catch (e) { previewing = /[?&]fl_search=1\b/.test(location.search); }
  if (!CFG.enabled && !previewing) return;
  document.documentElement.classList.add('flss-on');

  const $ = (sel) => dialog.querySelector(sel);
  const input = $('.flss-input');
  const elClear = $('.flss-clear');
  const elUnderstood = $('.flss-understood');
  const elSide = $('.flss-side');
  const elMain = $('.flss-main');
  const elStatus = $('.flss-live[role="status"]');

  /* ------------------------------------------------------------------ text helpers */
  const norm = (s) =>
    String(s == null ? '' : s)
      .toLowerCase()
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[’'`´]/g, '')
      .replace(/&/g, ' and ')
      .replace(/[^a-z0-9]+/g, ' ')
      .trim();
  const esc = (s) => String(s == null ? '' : s).replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
  const money = (n) => '$' + Number(n).toLocaleString('en-US', { minimumFractionDigits: n % 1 ? 2 : 0, maximumFractionDigits: 2 });
  const plural = (n, one, many) => `${n.toLocaleString('en-US')} ${n === 1 ? one : many || one + 's'}`;

  /** Edit distance with a ceiling: stops as soon as the words are further apart than `max`. */
  function distance(a, b, max) {
    const la = a.length, lb = b.length;
    if (Math.abs(la - lb) > max) return max + 1;
    let prev2 = null, prev = new Array(lb + 1), cur = new Array(lb + 1);
    for (let j = 0; j <= lb; j++) prev[j] = j;
    for (let i = 1; i <= la; i++) {
      cur[0] = i;
      let rowMin = cur[0];
      for (let j = 1; j <= lb; j++) {
        const cost = a.charCodeAt(i - 1) === b.charCodeAt(j - 1) ? 0 : 1;
        let v = Math.min(prev[j] + 1, cur[j - 1] + 1, prev[j - 1] + cost);
        if (i > 1 && j > 1 && a.charCodeAt(i - 1) === b.charCodeAt(j - 2) && a.charCodeAt(i - 2) === b.charCodeAt(j - 1)) v = Math.min(v, prev2[j - 2] + 1);
        cur[j] = v;
        if (v < rowMin) rowMin = v;
      }
      if (rowMin > max) return max + 1;
      prev2 = prev; prev = cur; cur = new Array(lb + 1);
    }
    return prev[lb];
  }

  /** How well a typed word `q` stands for a catalogue word `c`: 1 exact … 0 not at all. */
  function quality(q, c, typing) {
    if (q === c) return 1;
    const lq = q.length, lc = c.length;
    if (lq >= 2 && lc > lq && c.startsWith(q)) return typing ? 0.9 : lq >= 3 ? 0.8 : 0.55;
    if (lq >= 4 && q.charCodeAt(0) === c.charCodeAt(0)) {
      const max = lq >= 8 ? 2 : 1;
      const d = distance(q, c, max);
      if (d <= max) return d === 1 ? 0.72 : 0.55;
    }
    if (lq >= 5 && Math.abs(lq - lc) <= 1 && distance(q, c, 1) <= 1) return 0.6; // first letter wrong
    if (lq >= 4 && lc > lq && c.includes(q)) return 0.45;
    return 0;
  }

  /* ------------------------------------------------------------------ what words mean */
  const STOP = new Set(
    'a an the and or with for of in on to that this is it i im me my we you your want wants looking look need needs something anything some any good nice great best perfume perfumes fragrance fragrances cologne colognes scent scents smell smells smelling parfum eau de edp edt extrait spray bottle please show find get buy can do does like similar'.split(' ')
  );
  const GENERIC = new Set('eau de parfum extrait edp edt spray oz ml fl the and by intense elixir cologne toilette perfume sample travel size'.split(' '));
  /** Short names people type for a house. Matched against the house names in the index, so only houses we carry show. */
  const HOUSE_ALIASES = {
    ysl: 'yves saint laurent', mfk: 'maison francis kurkdjian', pdm: 'parfums de marly', tf: 'tom ford', jpg: 'jean paul gaultier', ck: 'calvin klein',
    dg: 'dolce gabbana', 'd g': 'dolce gabbana', ch: 'carolina herrera', ga: 'giorgio armani', armani: 'giorgio armani', bdk: 'bdk parfums', lv: 'louis vuitton',
    mm: 'maison margiela', margiela: 'maison margiela', kilian: 'kilian', vc: 'van cleef', mdci: 'mdci', fm: 'frederic malle', malle: 'frederic malle',
    dior: 'dior', chanel: 'chanel', hermes: 'hermes', initio: 'initio', nishane: 'nishane', amouage: 'amouage', creed: 'creed', xerjoff: 'xerjoff',
  };

  /** A mood or style word → the accords and notes that carry it. Accord names are the index's fixed list. */
  const CONCEPTS = {
    sweet: { acc: ['sweet', 'gourmand', 'vanilla'], notes: ['vanilla', 'caramel', 'praline', 'honey', 'sugar', 'tonka bean', 'toffee', 'marshmallow'] },
    gourmand: { acc: ['gourmand', 'sweet', 'vanilla'], notes: ['vanilla', 'caramel', 'chocolate', 'praline', 'coffee', 'pistachio', 'almond'] },
    fresh: { acc: ['fresh', 'citrus', 'aquatic', 'green', 'aromatic', 'ozonic'], notes: ['bergamot', 'lemon', 'grapefruit', 'mint', 'neroli', 'sea notes', 'marine', 'green tea'] },
    clean: { acc: ['fresh', 'musky', 'white floral', 'powdery', 'ozonic'], notes: ['musk', 'white musk', 'aldehydes', 'cotton', 'lavender', 'neroli'] },
    citrus: { acc: ['citrus', 'fresh'], notes: ['bergamot', 'lemon', 'grapefruit', 'orange', 'mandarin orange', 'lime', 'yuzu', 'neroli', 'petitgrain'] },
    citrusy: { same: 'citrus' },
    woody: { acc: ['woody', 'oud', 'earthy', 'smoky'], notes: ['sandalwood', 'cedar', 'vetiver', 'agarwood oud', 'guaiac wood', 'patchouli', 'cypress'] },
    wood: { same: 'woody' },
    smoky: { acc: ['smoky', 'leather', 'tobacco', 'woody'], notes: ['incense', 'birch', 'tobacco', 'leather', 'cade', 'guaiac wood', 'olibanum'] },
    smokey: { same: 'smoky' },
    spicy: { acc: ['spicy', 'warm spicy'], notes: ['pink pepper', 'pepper', 'cardamom', 'cinnamon', 'saffron', 'clove', 'nutmeg', 'ginger'] },
    warm: { acc: ['amber', 'warm spicy', 'vanilla', 'balsamic', 'sweet'], notes: ['amber', 'vanilla', 'benzoin', 'tonka bean', 'labdanum', 'cinnamon'] },
    cozy: { same: 'warm' },
    fruity: { acc: ['fruity', 'tropical', 'sweet'], notes: ['peach', 'apple', 'pineapple', 'black currant', 'pear', 'plum', 'raspberry', 'mango', 'cherry', 'lychee', 'strawberry'] },
    fruit: { same: 'fruity' },
    floral: { acc: ['floral', 'white floral', 'rose'], notes: ['rose', 'jasmine', 'tuberose', 'iris', 'orange blossom', 'ylang ylang', 'peony', 'lily', 'violet'] },
    flowery: { same: 'floral' },
    flowers: { same: 'floral' },
    powdery: { acc: ['powdery', 'musky'], notes: ['iris', 'orris', 'violet', 'heliotrope', 'musk'] },
    musky: { acc: ['musky', 'powdery', 'animalic'], notes: ['musk', 'white musk', 'ambrette', 'cashmeran'] },
    boozy: { acc: ['boozy', 'warm spicy'], notes: ['rum', 'cognac', 'whiskey', 'wine', 'champagne', 'bourbon'] },
    leathery: { acc: ['leather', 'animalic', 'smoky'], notes: ['leather', 'suede', 'birch'] },
    green: { acc: ['green', 'aromatic', 'fresh'], notes: ['galbanum', 'fig leaf', 'grass', 'basil', 'mint', 'green tea', 'violet leaf'] },
    aquatic: { acc: ['aquatic', 'ozonic', 'fresh'], notes: ['sea notes', 'marine', 'sea salt', 'seaweed', 'water notes', 'calone'] },
    ocean: { same: 'aquatic' },
    creamy: { acc: ['creamy', 'vanilla', 'tropical'], notes: ['milk', 'sandalwood', 'coconut', 'vanilla', 'cream', 'tonka bean'] },
    earthy: { acc: ['earthy', 'woody'], notes: ['patchouli', 'vetiver', 'oakmoss', 'moss'] },
    dark: { acc: ['oud', 'smoky', 'leather', 'amber', 'balsamic'], notes: ['agarwood oud', 'incense', 'patchouli', 'leather', 'tobacco', 'labdanum'] },
    tropical: { acc: ['tropical', 'fruity'], notes: ['coconut', 'mango', 'pineapple', 'tiare flower', 'passion fruit', 'banana'] },
    beach: { acc: ['tropical', 'aquatic', 'citrus'], notes: ['coconut', 'sea notes', 'sea salt', 'tiare flower', 'monoi'], season: 1 },
    sexy: { acc: ['amber', 'musky', 'vanilla', 'animalic', 'warm spicy'], notes: ['amber', 'musk', 'vanilla', 'tonka bean', 'agarwood oud'], night: true },
    seductive: { same: 'sexy' },
    elegant: { acc: ['powdery', 'floral', 'woody'], notes: ['iris', 'rose', 'sandalwood', 'jasmine', 'musk'] },
    classy: { same: 'elegant' },
    unique: { acc: ['smoky', 'animalic', 'leather', 'oud', 'green'], notes: ['incense', 'agarwood oud', 'leather', 'saffron'] },
    barbershop: { acc: ['aromatic', 'fresh', 'powdery'], notes: ['lavender', 'oakmoss', 'geranium', 'tonka bean', 'coumarin'] },
    campfire: { same: 'smoky' }, bonfire: { same: 'smoky' }, fireplace: { same: 'smoky' }, smoke: { same: 'smoky' }, burnt: { same: 'smoky' },
    laundry: { same: 'clean' }, soap: { same: 'clean' }, soapy: { same: 'clean' }, shower: { same: 'clean' },
    dessert: { same: 'gourmand' }, candy: { same: 'gourmand' }, sugary: { same: 'gourmand' }, bakery: { same: 'gourmand' }, cake: { same: 'gourmand' }, edible: { same: 'gourmand' },
    forest: { same: 'woody' }, woods: { same: 'woody' },
    sea: { same: 'aquatic' }, rain: { same: 'aquatic' }, marine: { same: 'aquatic' },
    cosy: { same: 'warm' }, comforting: { same: 'warm' },
    sensual: { same: 'sexy' }, sophisticated: { same: 'elegant' },
    garden: { same: 'floral' }, bouquet: { same: 'floral' },
    cocktail: { same: 'boozy' }, gym: { same: 'fresh' }, sporty: { same: 'fresh' },
  };
  /** Words that are not a product word but a filter. The index says Masculine / Feminine / Unisex. */
  const GENDER = {
    men: 'Masculine', mens: 'Masculine', man: 'Masculine', male: 'Masculine', masculine: 'Masculine', him: 'Masculine', his: 'Masculine', husband: 'Masculine', boyfriend: 'Masculine', dad: 'Masculine', father: 'Masculine', guy: 'Masculine', guys: 'Masculine', gentleman: 'Masculine', homme: 'Masculine',
    women: 'Feminine', womens: 'Feminine', woman: 'Feminine', female: 'Feminine', feminine: 'Feminine', her: 'Feminine', hers: 'Feminine', wife: 'Feminine', girlfriend: 'Feminine', mom: 'Feminine', mother: 'Feminine', lady: 'Feminine', ladies: 'Feminine', girl: 'Feminine', femme: 'Feminine',
    unisex: 'Unisex',
  };
  const SEASON = { spring: 0, summer: 1, hot: 1, heat: 1, vacation: 1, holiday: 1, fall: 2, autumn: 2, winter: 3, cold: 3, christmas: 3, snow: 3 };
  const SEASON_NAME = ['Spring', 'Summer', 'Fall', 'Winter'];
  /** Occasions the index carries as letters: [the words, the letter, evening or daytime, the mood that goes with it, the label]. */
  const OCCASIONS = [
    [/\b(?:date nights?|first dates?|(?:for|on) (?:a |the |my )?dates?|romantic|romance|valentines?(?: day)?|anniversary)\b/g, 'd', 'night', 'sexy', 'Date night'],
    [/\b(?:night out|nights out|evening out|going out|clubbing|club|party|parties|dinners?)\b/g, 'e', 'night', 'sexy', 'Evening'],
    [/\b(?:office|work|workplace|professional|interviews?|school)\b/g, 'o', 'day', 'clean', 'Office'],
    [/\b(?:everyday|every day|daily|casual)\b/g, 'x', 'day', '', 'Everyday'],
    [/\b(?:special occasions?|weddings?|formal|gala|black tie)\b/g, 's', '', 'elegant', 'Special occasion'],
  ];
  const NIGHT = new Set(['night', 'evening', 'date', 'club', 'clubbing', 'party', 'dinner', 'romantic']);
  const DAY = new Set(['day', 'daytime', 'office', 'work', 'school', 'gym', 'daily', 'everyday', 'casual', 'brunch']);
  /** Words that ask for samples, travel sprays and sets: those are otherwise shown after the full bottles. */
  const SIDE_WORDS = /\b(samples?|travel|mini|minis|decants?|vials?|discovery|sets?|gift sets?|tester)\b/;

  /* ------------------------------------------------------------------ the index */
  let INDEX = null, loading = null, loadedAt = 0, refreshing = null, version = '';
  /* A page left open for hours, or one the phone brings back from its saved copy, would keep an old index (old prices,
     old stock). When the panel opens with an index older than ten minutes, /meta says whether a newer one exists. */
  const STALE_MS = 10 * 60 * 1000;
  function refreshIfStale() {
    if (!INDEX || !loadedAt || refreshing || Date.now() - loadedAt < STALE_MS) return;
    refreshing = fetch(CFG.meta, { credentials: 'omit', cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((m) => {
        loadedAt = Date.now();
        if (!m || !m.v || m.v === version) return null;
        return fetch(CFG.index + '?v=' + encodeURIComponent(m.v), { credentials: 'omit' }).then((r) => (r.ok ? r.json() : null)).then((ix) => {
          if (!ix || !ix.p || !ix.p.length) return;
          version = m.v;
          prepare(ix);
          try { if (dialog.open) update(); } catch (e) {}
        });
      })
      .catch(() => {})
      .then(() => { refreshing = null; });
  }
  window.addEventListener('pageshow', (e) => { if (e.persisted) { loadedAt = loadedAt ? 1 : 0; refreshIfStale(); } });
  document.addEventListener('visibilitychange', () => { if (!document.hidden) refreshIfStale(); });
  const VOCAB = new Map(); // word → [[productIndex, fieldWeight], …]
  const NAMEGRAMS = new Set(); // 2- and 3-word pieces of perfume names: "good girl", "le male", "the one"
  // Words that are filters when they stand alone; a name piece made only of these is not protected ("for her").
  const FILTER_ONLY = new Set('for her him men women man woman his hers pour homme femme and the of in on with'.split(' '));
  /** Famous houses people search for. The ones the store does not carry are worked out from the index at load time. */
  const KNOWN_HOUSES = ['chanel', 'calvin klein', 'kayali', 'byredo', 'diptyque', 'jo malone', 'juliette has a gun', 'glossier', 'ariana grande',
    'billie eilish', 'sol de janeiro', 'bath and body works', 'victorias secret', 'abercrombie', 'hollister', 'zara', 'clinique', 'estee lauder',
    'lancome', 'marc jacobs', 'michael kors', 'coach', 'ralph lauren', 'tommy hilfiger', 'davidoff', 'nautica', 'elizabeth arden', 'britney spears',
    'jimmy choo', 'montale', 'mancera', 'creed', 'xerjoff', 'nishane', 'amouage', 'le labo', 'maison margiela', 'frederic malle', 'kilian',
    'parfums de marly', 'initio', 'louis vuitton', 'hermes', 'prada', 'gucci', 'dior', 'yves saint laurent', 'giorgio armani', 'versace',
    'dolce and gabbana', 'tom ford', 'jean paul gaultier', 'paco rabanne', 'carolina herrera', 'viktor and rolf', 'valentino', 'burberry',
    'givenchy', 'guerlain', 'mugler', 'azzaro', 'montblanc', 'hugo boss', 'bvlgari', 'cartier', 'lattafa', 'armaf', 'afnan', 'rasasi',
    'ajmal', 'maison francis kurkdjian', 'byredo', 'escentric molecules', 'imaginary authors', 'dossier', 'phlur', 'skims', 'snif', 'ellis brooklyn',
    'vilhelm parfumerie', 'goldfield and banks', 'roja', 'clive christian', 'electimuss', 'bond no 9', 'atelier cologne', 'acqua di parma', 'penhaligons',
    'chloe', 'lacoste', 'issey miyake', 'kenzo', 'narciso rodriguez', 'moschino', 'ferragamo', 'boucheron', 'van cleef and arpels'];
  let NOT_CARRIED = [];
  let ENTITIES = [];       // houses, notes, accords, families: things with a page of their own
  let PRICE_LOW = 0, PRICE_HIGH = 0, IMG = '';

  const W = { name: 10, brand: 8, note: 5, accord: 4, family: 4, occasion: 2, gender: 1 };

  function addWords(text, pi, weight) {
    for (const w of norm(text).split(' ')) {
      if (!w) continue;
      let list = VOCAB.get(w);
      if (!list) VOCAB.set(w, (list = []));
      list.push([pi, weight]);
    }
  }

  function prepare(ix) {
    INDEX = ix;
    VOCAB.clear();
    NAMEGRAMS.clear();
    IMG = ix.img || '';
    const occName = {};
    for (const [, code, , , label] of OCCASIONS) occName[code] = label;
    ix.p.forEach((p, i) => {
      p.nb = norm(p.b);
      p.nt = norm(p.t);
      // The name without the house in front of it and without "Eau de Parfum Spray" at the end.
      let name = p.nt.startsWith(p.nb + ' ') ? p.nt.slice(p.nb.length + 1) : p.nt;
      p.nn = name.split(' ').filter((w) => !GENERIC.has(w) && !/^\d+(ml|oz|g|gr)$/.test(w)).join(' ') || name;
      // samples, travel sprays, sets, candles: shown after the full bottles unless asked for
      const side = p.nt.match(/\b(sample|travel|mini|decant|vial|discovery|set|kit|candle|lotion|body|deodorant|hair|mist|soap|cream|shower|gel|balm|gift card)\b/g);
      p.side = p.smp || side ? (side ? side.join(' ') : 'sample') : '';
      p.top = p.top || []; p.mid = p.mid || []; p.base = p.base || []; p.acc = p.acc || [];
      p.notes = [...p.top, ...p.mid, ...p.base];
      p.nnotes = p.notes.map(norm);
      // accords come as a ranked list: the first counts most
      p.accMap = new Map(p.acc.map((a, k) => [norm(a), Math.max(35, 90 - k * 10)]));
      p.occL = (p.occ || '').split('').map((c) => occName[c]).filter(Boolean);
      p.sn = String(p.s || '0000');
      addWords(p.nn, i, W.name);
      if (/(^| )1( |$)/.test(p.nn)) addWords('one', i, W.name);   // "one million" = "1 Million"
      { const nw = p.nn.split(' ');
        for (let k = 0; k < nw.length; k++) for (const len of [2, 3]) if (k + len <= nw.length) NAMEGRAMS.add(nw.slice(k, k + len).join(' ')); }
      addWords(p.nb, i, W.brand);
      for (const n of p.nnotes) addWords(n, i, W.note);
      for (const a of p.acc) addWords(a, i, W.accord);
      addWords(p.fam, i, W.family);
      for (const o of p.occL) addWords(o, i, W.occasion);
      addWords(p.g, i, W.gender);
      if (p.bt) addWords(p.bt, i, W.occasion);
      if (p.smp) addWords('sample travel', i, W.occasion);
    });
    // what a note is made of, for comparing perfumes: "tobacco leaf" → tobacco, "madagascar vanilla" → vanilla
    const single = new Map();
    for (const p of ix.p) for (const n of p.nnotes) if (!n.includes(' ')) single.set(n, (single.get(n) || 0) + 1);
    for (const p of ix.p) {
      const t = new Set();
      for (const n of p.nnotes) { t.add(n); for (const w of n.split(' ')) if (w !== n && (single.get(w) || 0) >= 5) t.add(w); }
      p.ntok = [...t];
      p._w = 0;
    }
    const df = new Map();
    for (const p of ix.p) for (const n of p.ntok) df.set(n, (df.get(n) || 0) + 1);
    IDF = new Map([...df].map(([n, c]) => [n, Math.log(1 + ix.p.length / c)]));
    const prices = ix.p.filter((p) => !p.smp).map((p) => p.p).filter(Boolean).sort((a, b) => a - b);
    PRICE_LOW = prices[Math.floor(prices.length * 0.3)] || 100;
    PRICE_HIGH = prices[Math.floor(prices.length * 0.75)] || 250;
    const ent = (rows, kind, label) => {
      const seen = new Map();
      for (const [name, handle, count] of rows || []) {
        const n = norm(name), had = seen.get(n);
        if (had) { had.count += count; if (!had.handle) had.handle = handle; }
        else seen.set(n, { kind, label, name, n, handle, count });
      }
      return [...seen.values()];
    };
    // famous houses with no products here (worked out from the brands in the index)
    { const have = (ix.brands || []).map(([name]) => norm(name).replace(/ /g, ''));
      NOT_CARRIED = KNOWN_HOUSES.filter((h) => { const c = h.replace(/ /g, ''); return !have.some((b) => b === c || b.includes(c) || (c.includes(b) && b.length >= 5)); }); }
    ENTITIES = [...ent(ix.brands, 'brand', 'Brand'), ...ent(ix.notes, 'note', 'Note'), ...ent(ix.accords, 'accord', 'Style'), ...ent(ix.families, 'family', 'Scent type')];
    // a brand page that the store does not have becomes the store's own vendor page
    for (const e of ENTITIES) if (e.kind === 'brand') { e.short = e.n.split(' ').filter((w) => !/^(parfums?|perfumes?|paris|london|new york|fragrances?|the|de|maison)$/.test(w)).join(' '); e.url = e.handle ? '/collections/' + e.handle : '/collections/vendors?q=' + encodeURIComponent(e.name); }
  }

  function fetchIndex(k) {
    useApi(APIS[k]);
    return fetch(CFG.index, { credentials: 'omit' }).then((r) => {
      if (!r.ok) throw new Error('index ' + r.status);
      return r.json();
    }, (e) => (k + 1 < APIS.length ? fetchIndex(k + 1) : Promise.reject(e)));
  }

  function load() {
    if (INDEX) return Promise.resolve(INDEX);
    if (!loading) {
      loading = fetchIndex(0)
        .then((ix) => (prepare(ix), (loadedAt = Date.now()), ix))
        .catch((e) => {
          loading = null;
          throw e;
        });
    }
    return loading;
  }

  /* ------------------------------------------------------------------ reading the request */
  function parse(rawQuery) {
    let text = ' ' + String(rawQuery).toLowerCase().replace(/[’`´]/g, "'") + ' ';
    const out = { raw: rawQuery, min: 0, max: 0, gender: '', season: -1, night: false, day: false, newest: false, inStock: false, niche: '', side: false, like: '', not: [], concepts: [], occasions: [], terms: [], chips: [] };
    const take = (re, fn) => {
      text = text.replace(re, (...m) => {
        const keep = fn(...m);
        return typeof keep === 'string' ? ` ${keep} ` : ' ';
      });
    };
    // money
    take(/(?:between|from)\s*\$?\s*(\d{2,4})\s*(?:and|to|-)\s*\$?\s*(\d{2,4})/g, (_, a, b) => { out.min = Math.min(+a, +b); out.max = Math.max(+a, +b); });
    take(/\$\s*(\d{2,4})\s*(?:-|to)\s*\$?\s*(\d{2,4})/g, (_, a, b) => { out.min = Math.min(+a, +b); out.max = Math.max(+a, +b); });
    take(/(?:under|below|less than|cheaper than|max(?:imum)?|up to|no more than|<)\s*\$?\s*(\d{2,4})(?:\s*(?:dollars|bucks|usd))?/g, (_, a) => { out.max = +a; });
    take(/(?:over|above|more than|at least|min(?:imum)?|>)\s*\$?\s*(\d{2,4})(?:\s*(?:dollars|bucks|usd))?/g, (_, a) => { out.min = +a; });
    take(/\$\s*(\d{2,4})\b/g, (_, a) => { if (!out.max) out.max = +a; });
    take(/\b(cheap|cheapest|affordable|budget|inexpensive)\b/g, () => { if (!out.max) { out.max = PRICE_LOW; out.cheap = true; } });
    take(/\b(luxury|luxurious|expensive|high end|premium)\b/g, () => { if (!out.min) { out.min = PRICE_HIGH; out.lux = true; } });
    // "like X"
    take(/\b(?:smells? like|similar to|something like|alternative to|dupe (?:for|of)|clone of|inspired by|in the style of|reminds me of|like)\s+(?!to\b)(.+?)\s*(?=$|\bbut\b|\bfor\b|\bunder\b|\bbelow\b|\bwith\b)/, (_, ref) => {
      // "smells like a campfire" is a mood, not a perfume to compare with
      const mood = norm(ref).split(' ').filter((w) => w && !/^(a|an|the|some|my|fresh|clean)$/.test(w));
      if (mood.length && mood.every((w) => CONCEPTS[w])) return norm(ref);
      out.like = ref.trim();
    });
    take(/\b(?:not too|not|no|without|non|less)\s+([a-z]+)/g, (_, w) => { out.not.push(w); });
    take(/\b(new arrivals?|newest|latest|just in|new in|new releases?)\b/g, () => { out.newest = true; });
    take(/\b(in stock|available)\b/g, () => { out.inStock = true; });
    take(/\b(on sale|sale|deals?|discounted|clearance)\b/g, () => { out.sale = true; });
    take(/\b(niche|designer)\b/g, (w) => { out.niche = w.trim() === 'niche' ? 'Niche' : 'Designer'; });
    take(/\b(samples?|travel (?:sizes?|sprays?)|travel|minis?|decants?|vials?|discovery sets?|testers?)\b/g, () => { out.side = true; out.sideOnly = true; });
    if (SIDE_WORDS.test(text)) out.side = true;
    for (const [re, code, when, mood, label] of OCCASIONS) {
      take(re, () => {
        if (!out.occasions.some((o) => o.code === code)) out.occasions.push({ code, mood, label });
        if (when === 'night') out.night = true;
        if (when === 'day') out.day = true;
      });
    }

    const words = norm(text).split(' ').filter(Boolean);
    const named = new Set();     // "good girl", "le male", "the one": a perfume's name, not a filter
    if (NAMEGRAMS.size) for (let k = 0; k < words.length; k++) for (const len of [3, 2]) {
      if (k + len > words.length) continue;
      const span = words.slice(k, k + len);
      if (span.every((w) => FILTER_ONLY.has(w))) continue;
      const whole = len === words.length;
      if (!whole && (FILTER_ONLY.has(span[0]) || FILTER_ONLY.has(span[len - 1]))) continue;      // "vanilla for" (her)
      if (whole && span.some((w) => CONCEPTS[w]) && span.some((w) => FILTER_ONLY.has(w) || GENDER[w])) continue;   // "fresh for men"
      if (!span.some((w) => STOP.has(w) || GENDER[w] || w in SEASON || NIGHT.has(w) || DAY.has(w))) continue;     // nothing to protect
      const g = span.join(' ');
      if (NAMEGRAMS.has(g) || NAMEGRAMS.has(g.replace(/\bone\b/g, '1'))) { for (let j = k; j < k + len; j++) named.add(j); break; }
    }
    for (let k = 0; k < words.length; k++) {
      const w = words[k];
      if (named.has(k)) { out.terms.push(w); continue; }
      if (GENDER[w]) { out.gender = out.gender && out.gender !== GENDER[w] ? 'Unisex' : GENDER[w]; continue; }
      if (w in SEASON && !(w === 'fall' && words[k + 1] === 'in')) { out.season = SEASON[w]; continue; }
      if (NIGHT.has(w)) { out.night = true; if (w !== 'date' && w !== 'dinner' && w !== 'romantic' && w !== 'party') continue; }
      if (DAY.has(w)) { out.day = true; if (w === 'day' || w === 'daytime' || w === 'daily' || w === 'everyday' || w === 'casual') continue; }
      let c = CONCEPTS[w];
      if (c && c.same) c = CONCEPTS[c.same];
      if (c) { out.concepts.push({ word: w, ...c }); if (c.season != null && out.season < 0) out.season = c.season; if (c.night) out.night = true; }
      if (STOP.has(w)) continue;
      out.terms.push(w);
    }
    // what was understood, in the shopper's words
    if (out.like) out.chips.push('Like ' + out.like);
    for (const c of out.concepts) out.chips.push(c.word[0].toUpperCase() + c.word.slice(1));
    if (out.gender) out.chips.push(out.gender === 'Masculine' ? 'Men' : out.gender === 'Feminine' ? 'Women' : 'Unisex');
    if (out.season >= 0) out.chips.push(SEASON_NAME[out.season]);
    for (const o of out.occasions) out.chips.push(o.label);
    if (out.night && !out.occasions.length) out.chips.push('Evening');
    if (out.day && !out.occasions.length) out.chips.push('Daytime');
    if (out.min && out.max) out.chips.push(`${money(out.min)}–${money(out.max)}`);
    else if (out.max) out.chips.push(`Under ${money(out.max)}`);
    else if (out.min) out.chips.push(`Over ${money(out.min)}`);
    for (const n of out.not) out.chips.push('Not ' + n);
    if (out.niche) out.chips.push(out.niche);
    if (out.sideOnly) out.chips.push('Travel sizes & samples');
    if (out.newest) out.chips.push('Newest first');
    if (out.inStock) out.chips.push('In stock');
    if (out.sale) out.chips.push('On sale');
    return out;
  }

  const REF_SKIP = new Set('for men women him her pour homme femme man woman his hers'.split(' '));
  /** The catalogue product a "like …" phrase points at, if we carry it (full bottles first). */
  function findReference(ref) {
    const words = norm(ref).split(' ').filter((w) => w && !GENERIC.has(w) && !REF_SKIP.has(w));
    if (!words.length) return null;
    let best = null, bestScore = 0;
    INDEX.p.forEach((p) => {
      const hay = (p.nb + ' ' + p.nt).split(' ').filter((w) => !REF_SKIP.has(w) && !GENERIC.has(w));
      let s = 0;
      for (const w of words) {
        let q = 0;
        for (const h of hay) q = Math.max(q, quality(w, h, false));
        if (!q) return (s = -1);
        s += q;
      }
      if (s <= 0) return;
      s = s / words.length - Math.abs(p.nn.split(' ').filter((w) => !REF_SKIP.has(w)).length - words.filter((w) => !p.nb.split(' ').includes(w)).length) * 0.02 - (p.side ? 0.05 : 0) + Math.min(p.n || 0, 50) / 2000 + (p.av ? 0.01 : 0) + Math.min(p.notes.length, 10) / 2000;
      if (s > bestScore) { bestScore = s; best = p; }
    });
    return bestScore >= 0.7 ? best : null;
  }

  /* Shared notes, weighted by how rare each note is: sharing pineapple and birch with Aventus means more than sharing
     musk and bergamot, which half the catalogue has. */
  let IDF = new Map();
  const noteW = (n) => IDF.get(n) || 1;
  const selfW = (p) => p._w || (p._w = p.ntok.reduce((t, n) => t + noteW(n) ** 2, 0));
  function similarity(a, b) {
    if (a === b) return 0;
    const an = new Set(a.ntok);
    let shared = 0;
    for (const n of b.ntok) if (an.has(n)) shared += noteW(n) ** 2;
    const notes = shared ? shared / Math.sqrt(Math.max(selfW(a), 20) * Math.max(selfW(b), 20)) : 0;
    let acc = 0;
    for (const [n, pct] of a.accMap) if (b.accMap.has(n)) acc += Math.min(pct, b.accMap.get(n)) / 100;
    const fam = a.fam && a.fam === b.fam ? 0.12 : 0;
    return notes * 4 + acc * 0.08 + fam;
  }

  /** Does this bottle fit the price, gender and stock limits of the request? */
  function within(q, p) {
    if (q.max && p.p > q.max) return false;
    if (q.min && p.p < q.min) return false;
    if (q.gender === 'Masculine' && p.g === 'Feminine') return false;
    if (q.gender === 'Feminine' && p.g === 'Masculine') return false;
    if (q.gender === 'Unisex' && p.g && p.g !== 'Unisex') return false;
    if (q.inStock && !p.av) return false;
    if (q.sale && !(p.cp && p.cp > p.p)) return false;
    if (q.niche && p.bt && p.bt !== q.niche) return false;
    // a price ceiling is about full bottles, unless samples were asked for
    if ((q.max || q.min) && p.side && !q.side) return false;
    if (q.sideOnly && !p.side) return false;
    return true;
  }

  /** The house the words name: "dior", "ysl", "mfk", "parfums de marly". One house can sit under two vendor names
      ("Maison Francis Kurkdjian" and "Maison Francis Kurkdjian Paris"): all of them come back as one group. */
  function houseFor(phrase) {
    if (!phrase || phrase.length < 2) return null;
    const alias = HOUSE_ALIASES[phrase];
    const target = alias || phrase;
    const tflat = target.replace(/ /g, '');
    const isNote = ENTITIES.some((e) => e.kind !== 'brand' && e.n === target);
    const brands = ENTITIES.filter((e) => e.kind === 'brand');
    const scoreOf = (e) => {
      const words = e.n.split(' '), sw = (e.short || e.n).split(' ');
      if (e.n === target || e.short === target || (tflat.length >= 5 && (e.n.replace(/ /g, '') === tflat || (e.short || '').replace(/ /g, '') === tflat))) return 3;
      if (isNote) return 0;                                                    // "oud", "rose", "amber": a note first
      if (target.length >= 3 && e.n.startsWith(target + ' ')) return 2.5;     // "maison francis" → Maison Francis Kurkdjian
      if (target.length >= 3 && (sw[sw.length - 1] === target || words[words.length - 1] === target) && sw.length <= 2) return 2.4; // "dior" → Christian Dior
      if (alias && e.n.includes(target)) return 2.2;
      if (target.length >= 5 && e.short && distance(target, e.short, 2) <= (target.length >= 8 ? 2 : 1)) return 1.8; // "creeed", "amouge"
      return 0;
    };
    let best = null, bestS = 0;
    for (const e of brands) { const v = scoreOf(e) + Math.min(e.count, 100) / 1000; if (v > bestS) { bestS = v; best = e; } }
    if (!best || bestS < 1.8) return null;
    // the same house under other vendor names
    const core = best.short || best.n;
    const flat = (x) => x.replace(/ /g, '');
    const group = brands.filter((e) => e === best || (core.length >= 4 && ((e.short || e.n) === core || flat(e.short || e.n) === flat(core) || flat(e.n) === flat(core) || e.n.startsWith(core + ' ') || core.startsWith((e.short || e.n) + ' '))));
    const main = group.reduce((a, e) => (e.count > a.count ? e : a), best);
    return { name: display(main.name), n: main.n, url: main.url, count: group.reduce((a, e) => a + e.count, 0), members: new Set(group.map((e) => e.n)), entities: group };
  }
  /** "YVES SAINT LAURENT" → "Yves Saint Laurent" for display; short names (BDK, MDCI) stay as they are. */
  function display(name) {
    return name.length > 5 && name === name.toUpperCase() ? name.toLowerCase().replace(/(^|[\s\-'(])([a-z])/g, (m, a, b) => a + b.toUpperCase()) : name;
  }

  /* ------------------------------------------------------------------ ranking */
  /** "flower bomb" → flowerbomb, "tomford" → tom ford, "lightblue" → light blue: whichever the catalogue has. */
  function rewriteTerms(terms) {
    const out = [];
    for (let k = 0; k < terms.length; k++) {
      const a = terms[k], b = terms[k + 1];
      if (b && VOCAB.has(a + b) && a.length >= 2 && b.length >= 2) { out.push(a + b); k++; continue; }
      if (!VOCAB.has(a) && a.length >= 6 && !/^\d/.test(a)) {
        let split = null;
        for (let i = 2; i <= a.length - 2 && !split; i++) { const x = a.slice(0, i), y = a.slice(i); if (VOCAB.has(x) && VOCAB.has(y) && x.length + y.length >= 6) split = [x, y]; }
        if (split) { out.push(...split); continue; }
      }
      out.push(a);
    }
    return out;
  }

  /** A famous house the store does not carry, if the words name one ("chanel", "bleu de chanel", "calvin klien", "ck one"). */
  function notCarried(terms) {
    if (!NOT_CARRIED.length || !terms.length) return null;
    const words = terms.map((t) => (HOUSE_ALIASES[t] && !ENTITIES.some((e) => e.kind === 'brand' && e.n.includes(HOUSE_ALIASES[t])) ? HOUSE_ALIASES[t] : t)).join(' ').split(' ');
    for (const h of NOT_CARRIED) {
      const hw = h.split(' ');
      for (let k = 0; k + hw.length <= words.length; k++) {
        let ok = true;
        for (let j = 0; j < hw.length && ok; j++) { const w = words[k + j], c = hw[j]; ok = w === c || (c.length >= 5 && w.length >= 4 && distance(w, c, c.length >= 8 ? 2 : 1) <= (c.length >= 8 ? 2 : 1)); }
        if (ok) return { name: h.replace(/(^| )([a-z])/g, (m, a, b) => a + b.toUpperCase()).replace(/\bAnd\b/g, '&'), at: k, len: hw.length };
      }
    }
    return null;
  }

  function search(rawQuery) {
    const q = parse(rawQuery);
    const nc = notCarried(q.terms);
    if (nc) q.terms.splice(nc.at, nc.len);       // the house is not here: do not "correct" it into another word
    q.terms = rewriteTerms(q.terms);
    const n = INDEX.p.length;
    const score = new Float32Array(n);
    const hit = new Uint8Array(n);
    const fuzzy = [];
    const read = [];
    let unknown = 0;
    const matched = Array.from({ length: n }, () => null);
    let reference = null;

    // a house typed by itself, or a nickname for one ("dior", "ysl", "mfk")
    const house = houseFor(q.terms.join(' '));
    const typing = /\S$/.test(rawQuery);
    const soft = new Set();
    q.terms.forEach((term, ti) => {
      const per = new Float32Array(n);
      const isLast = typing && ti === q.terms.length - 1;
      let bestWord = '', bestQ = 0, bestCount = 0;
      for (const [word, list] of VOCAB) {
        const ql = quality(term, word, isLast);
        if (!ql) continue;
        if (ql > bestQ || (ql === bestQ && list.length > bestCount)) { bestQ = ql; bestWord = word; bestCount = list.length; }
        for (const [pi, weight] of list) {
          const v = ql * weight;
          if (v > per[pi]) {
            per[pi] = v;
            (matched[pi] || (matched[pi] = new Set())).add(word);
          }
        }
      }
      if (house && HOUSE_ALIASES[term]) { soft.add(ti); return; } // "ysl": the house, not the letters
      if (bestQ < 0.55 && (CONCEPTS[term] || DAY.has(term) || NIGHT.has(term))) { soft.add(ti); return; }
      if (CONCEPTS[term] && q.terms.length > 1) {
        soft.add(ti);
        for (let i = 0; i < n; i++) if (per[i]) score[i] += per[i] * 0.35;
        return;
      }
      if (bestWord && bestQ < 0.8 && bestQ >= 0.55) fuzzy.push([term, bestWord]);
      if (bestQ < 0.55) unknown++;
      read.push(bestWord && bestQ >= 0.55 && bestQ < 1 && !isLast ? bestWord : bestWord && bestQ >= 0.55 && bestQ < 0.8 ? bestWord : term);
      for (let i = 0; i < n; i++) if (per[i]) { score[i] += per[i]; hit[i]++; }
    });
    const terms = q.terms.filter((t, ti) => !soft.has(ti));
    if (house) INDEX.p.forEach((p, i) => { if (house.members.has(p.nb)) { score[i] += 12; hit[i] = Math.max(hit[i], terms.length); } });

    for (const c of q.concepts) {
      INDEX.p.forEach((p, i) => {
        let s = 0;
        (c.acc || []).forEach((a, k) => { if (p.accMap.has(a)) s = Math.max(s, (p.accMap.get(a) / 100) * (k === 0 ? 1 : k < 3 ? 0.75 : 0.55)); });
        let ns = 0;
        for (const nn of c.notes || []) if (p.nnotes.some((x) => x === nn || x.includes(nn))) ns++;
        const v = s * 3 + Math.min(ns, 3) * 1.1;
        if (v > 0) score[i] += v;
      });
    }

    for (const o of q.occasions) {
      let c = o.mood ? CONCEPTS[o.mood] : null;
      if (c && c.same) c = CONCEPTS[c.same];
      INDEX.p.forEach((p, i) => {
        if (!(p.occ || '').includes(o.code)) return;
        let v = 2.2;
        if (c) {
          let a = 0;
          (c.acc || []).forEach((x, k) => { if (p.accMap.has(x)) a = Math.max(a, (p.accMap.get(x) / 100) * (k === 0 ? 1 : 0.75)); });
          v += a * 2.5;
        }
        score[i] += v;
      });
    }

    if (q.like && INDEX) {
      reference = findReference(q.like);
      if (reference) INDEX.p.forEach((p, i) => { const s = similarity(reference, p); if (s > 0.3) { score[i] += s * 6; hit[i] = Math.max(hit[i], 1); } });
    }

    const needTerms = terms.length;
    const hasIntent = needTerms || q.concepts.length || q.occasions.length || reference || house;
    const hasFilter = q.gender || q.max || q.min || q.season >= 0 || q.night || q.day || q.newest || q.inStock || q.niche || q.side || q.sale;
    const out = [];
    const typed = q.terms.join(' ');
    const phrase = terms.length ? terms.join(' ') : typed;
    const readPhrase = terms.length ? read.join(' ') : typed;
    INDEX.p.forEach((p, i) => {
      if (!within(q, p)) return;
      let s = score[i];
      if (needTerms) {
        if (!hit[i]) { if (!(q.concepts.length && s > 0) && !reference) return; }
        const part = needTerms ? hit[i] / needTerms : 1;
        s *= part * part;
        if (phrase.length >= 3) {
          if (p.nn === phrase) s += 14;
          else if (p.nn.startsWith(phrase)) s += 9;
          else if ((p.nb + ' ' + p.nn).includes(phrase)) s += 6;
          else if (p.nb === phrase || p.nb.startsWith(phrase)) s += 4;
          if (readPhrase !== phrase && readPhrase.length >= 3 && (p.nb + ' ' + p.nn).includes(readPhrase)) s += 6; // "baccarat rouje" → "baccarat rouge"
        }
        if (needTerms > 1 && hit[i] === needTerms) {
          if (p.nnotes.includes(readPhrase)) s += 7;
          else if (p.accMap.has(readPhrase)) s += 5 * (p.accMap.get(readPhrase) / 100) + 2;
          else if (norm(p.fam) === readPhrase) s += 5;
        }
      } else if (hasIntent) {
        if (s <= 0) return;
        if (typed.length >= 3 && (p.nn === typed || p.nn.startsWith(typed + ' '))) s += 9;
      } else if (!hasFilter) return;
      else s = 1 + Math.min(p.n || 0, 40) / 40 + (p.r || 0) / 10;
      for (const w of q.not) {
        let c = CONCEPTS[w];
        if (c && c.same) c = CONCEPTS[c.same];
        const accs = c ? c.acc : [w];
        let pen = 0;
        for (const a of accs) if (p.accMap.has(a)) pen = Math.max(pen, p.accMap.get(a));
        if (p.nnotes.some((x) => x === w || x.split(' ').includes(w))) pen = Math.max(pen, 70);
        if (pen >= 70) return;
        if (pen) s *= 1 - pen / 140;
      }
      if (q.season >= 0) s *= /1/.test(p.sn) ? (p.sn[q.season] === '1' ? 1.15 : 0.55) : 0.8;
      if (q.night) s *= /[de]/.test(p.occ || '') ? 1.1 : 0.85;
      if (q.day) s *= /[ox]/.test(p.occ || '') ? 1.1 : 0.85;
      if (q.gender && p.g === q.gender) s *= 1.15;
      if (q.niche && p.bt === q.niche) s *= 1.1;
      // best sellers and well-reviewed bottles a little ahead of equal matches
      s *= 1 + Math.min(p.n || 0, 30) / 300 + (p.r >= 4.5 && p.rc >= 5 ? 0.04 : 0);
      if (!p.av) s *= 0.45;
      if (p.side && !q.side && !terms.some((t) => p.side.includes(t))) s *= 0.3;
      if (s > 0) out.push([s, i]);
    });
    if (out.length > 1 && hasIntent && !q.newest && !(house && !needTerms)) {
      let best = 0;
      for (const [s] of out) if (s > best) best = s;
      const floor = needTerms ? Math.max(best * 0.12, 1.2) : reference ? Math.max(best * 0.3, 0.6) : Math.max(best * 0.4, 0.4);
      for (let k = out.length - 1; k >= 0; k--) if (out[k][0] < floor) out.splice(k, 1);
    }
    if (q.newest) out.sort((a, b) => (INDEX.p[b[1]].c > INDEX.p[a[1]].c ? 1 : INDEX.p[b[1]].c < INDEX.p[a[1]].c ? -1 : b[0] - a[0]));
    else out.sort((a, b) => b[0] - a[0] || INDEX.p[a[1]].p - INDEX.p[b[1]].p);
    // In stock first. A sold-out bottle stays ahead only when it is plainly what was typed (it scores over twice
    // the best bottle in stock), so "aventus" still shows Aventus when it is sold out.
    // The bottle that was named ("tom ford tobaco", "le male") counts as typed even when it is sold out.
    const namePhrase = (needTerms && !(house && !needTerms) ? readPhrase : '').trim();
    const isNamed = (p) => {
      if (namePhrase.length < 3 || house) return false;
      const hay = ' ' + p.nb + ' ' + p.nn + ' ';
      if (hay.includes(' ' + namePhrase + ' ') || hay.includes(' ' + namePhrase.replace(/\bone\b/g, '1') + ' ')) return true;
      const rest = namePhrase.split(' ').filter((w) => !p.nb.split(' ').includes(w)).join(' ');   // the name without the house words
      return rest.length >= 3 && rest !== namePhrase && (' ' + p.nn + ' ').includes(' ' + rest + ' ');
    };
    {
      const bestIn = out.find((x) => INDEX.p[x[1]].av);
      const cut = bestIn ? bestIn[0] * 2 : 0;
      const keep = (x) => INDEX.p[x[1]].av || x[0] > cut || isNamed(INDEX.p[x[1]]);
      const first = out.filter(keep);
      if (bestIn && first.length < out.length) out.splice(0, out.length, ...first, ...out.filter((x) => !keep(x)));
    }
    // bottles whose name is what was typed come first ("one million" → the 1 Million bottles before Lady Million)
    if (namePhrase.length >= 3 && !house) {
      const nm = out.filter((x) => isNamed(INDEX.p[x[1]]));
      if (nm.length && nm.length < out.length) out.splice(0, out.length, ...nm, ...out.filter((x) => !isNamed(INDEX.p[x[1]])));
    }
    // samples and sets after the full bottles, unless one was asked for; a sold-out named bottle keeps its sample beside it
    if (!q.side) {
      const full = out.filter((x) => !INDEX.p[x[1]].side), side = out.filter((x) => INDEX.p[x[1]].side);
      if (full.length) {
        const order = [];
        for (const x of full) {
          order.push(x);
          const p = INDEX.p[x[1]];
          if (!p.av && isNamed(p) && order.length < 8) {
            const k = side.findIndex((y) => { const s = INDEX.p[y[1]]; return s.av && s.nb === p.nb && (s.nn === p.nn || s.nn.startsWith(p.nn) || p.nn.startsWith(s.nn)); });
            if (k >= 0) order.push(side.splice(k, 1)[0]);
          }
        }
        out.splice(0, out.length, ...order, ...side);
      }
    }
    // "born in rome": label the correction with the word the top result has (roma), not the most common one (rose)
    if (fuzzy.length && out.length) {
      const top = matched[out[0][1]];
      if (top) for (const f of fuzzy) { for (const w of top) if (w !== f[1] && quality(f[0], w, false) >= 0.55) { const r = read.indexOf(f[1]); if (r >= 0) read[r] = w; f[1] = w; break; } }
    }
    // a short typo next to a house: "gui" → Gucci, Guerlain
    const suggest = [];
    if (!house && q.terms.length === 1 && q.terms[0].length >= 3 && q.terms[0].length <= 6 && !/^\d/.test(q.terms[0])) {
      const t = q.terms[0];
      for (const e of ENTITIES) {
        if (e.kind !== 'brand') continue;
        const ws = (e.short || e.n).split(' ');
        if (ws.some((w) => w.startsWith(t))) continue;          // already found as typed
        if (ws.some((w) => w.length > t.length && w[0] === t[0] && distance(t, w.slice(0, t.length), 1) <= 1)) suggest.push(e);
      }
      suggest.sort((a, b) => b.count - a.count);
    }

    // houses, notes, accords and families the words point at
    const ents = [];
    if (house) for (const e of house.entities) ents.push([9 + e.count / 1000, e]);
    for (const e of suggest.slice(0, 3)) ents.push([1.1 + Math.min(e.count, 60) / 600, e]);
    if (phrase.length >= 2) {
      for (const e of ENTITIES) {
        if (house && house.entities.includes(e)) continue;
        let s = 0;
        if (e.n === phrase || e.n === readPhrase) s = 3;
        else if (e.n.startsWith(phrase) || e.n.startsWith(readPhrase)) s = 2;
        else if ((' ' + e.n).includes(' ' + phrase)) s = 1.5;
        else if (q.terms.length && q.terms.every((t) => e.n.split(' ').some((w) => quality(t, w, typing) >= 0.6))) s = 1;
        else if (q.terms.length > 1) { for (const t of read) if (t.length >= 3 && e.n === t) s = Math.max(s, 0.9); }
        if (s) ents.push([s + Math.min(e.count, 60) / 600 + (e.kind === 'brand' ? 0.3 : 0), e]);
      }
      ents.sort((a, b) => b[0] - a[0]);
    }
    return {
      q, reference, house, notCarried: nc ? nc.name : '',
      total: out.length,
      top: out.length ? out[0][0] : 0,
      products: out.slice(0, 8).map(([s, i]) => ({ p: INDEX.p[i], s, words: matched[i] })),
      list: (k) => out.slice(0, k || out.length).map(([s, i]) => ({ p: INDEX.p[i], s, words: matched[i] })),
      readAs: read.join(' '),
      entities: ents.slice(0, 7).map((x) => x[1]),
      fuzzy, unknown, hasIntent, hasFilter,
    };
  }

  /* ------------------------------------------------------------------ the advisor (AI) */
  const aiCache = new Map();
  let aiAbort = null, aiTimer = 0, aiFor = '', aiDown = false;

  /** Is this a request the index cannot settle on its own? */
  function wantsAdvisor(res, raw) {
    if (!CFG.ai || aiDown) return false;
    const text = raw.trim();
    const words = text.split(/\s+/).length;
    if (text.length < 4 || res.house) return false;
    if (res.notCarried) return true;                 // "bleu de chanel": the closest things we do carry
    if (words >= 2 && res.fuzzy.length && res.total < 6) return true;
    if (res.q.like) return true;                     // "like Aventus": the AI knows how it smells, the index only its notes
    if (res.total === 0) return true;
    if (words >= 2 && res.unknown > 0) return true;
    if (words >= 2 && res.total < 3) return true;
    return false;
  }

  function askAdvisor(raw, res) {
    clearTimeout(aiTimer);
    if (aiAbort) aiAbort.abort();
    const key = norm(raw);
    aiFor = key;
    if (!wantsAdvisor(res, raw)) return renderAdvisor(null);
    if (aiCache.has(key)) return renderAdvisor(aiCache.get(key), raw);
    renderAdvisor('wait', raw);
    aiTimer = setTimeout(() => {
      aiAbort = new AbortController();
      const timeout = setTimeout(() => aiAbort && aiAbort.abort(), 9000);
      fetch(CFG.ai, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: JSON.stringify({ q: raw.slice(0, 200) }), signal: aiAbort.signal })
        .then((r) => {
          if (r.status === 503 || r.status === 502) aiDown = true;     // AI switched off or failing: stop asking this visit
          return r.ok ? r.json() : Promise.reject(new Error('advisor ' + r.status));
        })
        .then((d) => {
          clearTimeout(timeout);
          const picks = (d.picks || []).map((x) => ({ p: INDEX.p.find((p) => p.h === x.h), why: x.why || '' })).filter((x) => x.p);
          const answer = picks.length ? { say: d.say || '', picks } : null;
          aiCache.set(key, answer);
          if (aiFor === key) renderAdvisor(answer, raw);
        })
        .catch(() => {
          clearTimeout(timeout);
          if (aiFor !== key) return;
          renderAdvisor(null);
          const s = $('.flss-none-s');      // no AI answer: say so plainly instead of "Still looking…"
          if (s) s.innerHTML = 'Try a brand, a note or a budget: ' + ['oud', 'vanilla', 'under $75', 'women'].map((x) => chip(x)).join(' ');
        });
    }, 650);
  }

  /* ------------------------------------------------------------------ drawing */
  const imgUrl = (p, w) => (p.i ? (/^https?:/.test(p.i) ? p.i : IMG + p.i) + '?width=' + w : '');
  const img = (p, size) => (p.i ? `<img src="${esc(imgUrl(p, size * 2))}" alt="" decoding="async" fetchpriority="low" width="${size}" height="${size}" data-flss-h="${esc(p.h)}">` : '<span class="flss-noimg" aria-hidden="true"></span>');

  function mark(text, words) {
    if (!words || !words.size) return esc(text);
    return esc(text).replace(/[A-Za-zÀ-ÿ0-9]+/g, (w) => (words.has(norm(w)) ? `<mark>${w}</mark>` : w));
  }

  function notesLine(p, words, concepts) {
    const want = new Set();
    for (const c of concepts || []) for (const n of c.notes || []) want.add(n);
    const seen = new Set();
    const scored = [];
    p.notes.forEach((n, k) => {
      const nn = p.nnotes[k];
      if (seen.has(nn)) return;
      seen.add(nn);
      const direct = words && nn.split(' ').some((w) => words.has(w));
      const mood = [...want].some((w) => nn === w || nn.includes(w));
      scored.push({ n, v: direct ? 2 : mood ? 1 : 0, k });
    });
    scored.sort((a, b) => b.v - a.v || a.k - b.k);
    const pick = scored.slice(0, 4);
    if (!pick.length) return p.acc.slice(0, 3).map((a) => esc(a)).join(' · ');
    return pick.map((x) => (x.v ? `<mark>${esc(x.n)}</mark>` : esc(x.n))).join(' · ');
  }

  function price(p) {
    const sale = p.cp && p.cp > p.p;
    return (
      `<span class="flss-price${sale ? ' flss-price--sale' : ''}">${p.pv ? '<span class="flss-from">From</span> ' : ''}${money(p.p)}${sale ? ` <s>${money(p.cp)}</s>` : ''}</span>` +
      (p.sp && p.sp < p.p ? `<span class="flss-sp">Samples from ${money(p.sp)}</span>` : '') +
      (p.av ? '' : '<span class="flss-out">Sold out</span>')
    );
  }

  function card(p, opts = {}) {
    const name = p.t.toLowerCase().startsWith(p.b.toLowerCase() + ' ') ? p.t.slice(p.b.length + 1) : p.t;
    return (
      `<a class="flss-card${opts.ai ? ' flss-card--ai' : ''}${p.av ? '' : ' flss-card--out'}" href="/products/${esc(p.h)}" data-opt>` +
      `<span class="flss-img">${img(p, 180)}${p.cp && p.cp > p.p ? '<span class="flss-badge">Sale</span>' : ''}</span>` +
      `<span class="flss-info">` +
      `<span class="flss-brand">${mark(display(p.b), opts.words)}</span>` +
      `<span class="flss-name">${mark(name, opts.words)}</span>` +
      (opts.why ? `<span class="flss-why">${esc(opts.why)}</span>` : `<span class="flss-notes">${notesLine(p, opts.words, opts.concepts)}</span>`) +
      `<span class="flss-meta">${price(p)}${p.r && p.rc ? `<span class="flss-rating" aria-label="Rated ${p.r} out of 5">★ ${p.r.toFixed(1)} <span>(${p.rc})</span></span>` : ''}</span>` +
      `</span></a>`
    );
  }

  const ICON = '<svg class="flss-ico" viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M4 5h16v11H9l-5 4z" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linejoin="round"/><path d="M8 9.5h8M8 12.5h5" stroke="currentColor" stroke-width="1.6" stroke-linecap="round"/></svg>';

  function renderAdvisor(answer) {
    const box = $('.flss-ai');
    if (!box) return;
    if (!answer) { box.hidden = true; box.innerHTML = ''; return; }
    box.hidden = false;
    if (answer === 'wait') {
      box.innerHTML = `<div class="flss-ai-head">${ICON}<span>Finding fragrances for you…</span></div><div class="flss-ai-skel"><i></i><i></i><i></i></div>`;
      return;
    }
    box.innerHTML =
      `<div class="flss-ai-head">${ICON}<span>Picked for your request</span></div>` +
      (answer.say ? `<p class="flss-ai-say">${esc(answer.say)}</p>` : '') +
      `<div class="flss-grid flss-grid--ai">${answer.picks.slice(0, 4).map((x) => card(x.p, { ai: true, why: x.why })).join('')}</div>`;
    const none = $('.flss-none');
    if (none) none.hidden = true;
    announce(`${plural(answer.picks.length, 'fragrance')} picked for your request.`);
  }

  function announce(text) { if (elStatus) elStatus.textContent = text; }

  const recentKey = 'fl-search-recent';
  const recent = () => {
    try { return JSON.parse(localStorage.getItem(recentKey) || '[]').filter((x) => typeof x === 'string').slice(0, 6); } catch (e) { return []; }
  };
  const remember = (q) => {
    q = q.trim();
    if (q.length < 2) return;
    try { localStorage.setItem(recentKey, JSON.stringify([q, ...recent().filter((x) => x.toLowerCase() !== q.toLowerCase())].slice(0, 6))); } catch (e) { /* private window */ }
  };
  const forget = () => { try { localStorage.removeItem(recentKey); } catch (e) {} };

  const chip = (label, query) => `<button type="button" class="flss-chip" data-q="${esc(query || label)}">${esc(label)}</button>`;
  const searchHref = (raw) => `${CFG.searchUrl}?q=${encodeURIComponent(raw.trim())}&type=product`;

  function pickHouses(list, n) {
    const out = [], seen = new Set();
    for (const p of list) { if (seen.has(p.b)) continue; seen.add(p.b); out.push(p); if (out.length === n) break; }
    return out;
  }

  function renderHome() {
    elUnderstood.innerHTML = '';
    const rec = recent();
    elSide.innerHTML =
      (rec.length ? `<div class="flss-h">Your searches <button type="button" class="flss-forget">Clear</button></div><div class="flss-chips">${rec.map((r) => chip(r)).join('')}</div>` : '') +
      `<div class="flss-h${rec.length ? ' flss-h--gap' : ''}">Trending searches</div><div class="flss-chips">${CFG.trending.map((p) => chip(p)).join('')}</div>` +
      `<div class="flss-h flss-h--gap">Describe your scent</div><div class="flss-asks">${CFG.describe.map((a) => `<button type="button" class="flss-ask" data-q="${esc(a)}">${ICON}<span>${esc(a)}</span></button>`).join('')}</div>`;
    if (!INDEX) { elMain.innerHTML = '<div class="flss-loading" aria-hidden="true"><i></i><i></i><i></i><i></i></div>'; return; }
    const full = INDEX.p.filter((x) => x.av && x.i && !x.side);
    const best = pickHouses(full.filter((x) => x.n).sort((a, b) => b.n - a.n || (b.r || 0) - (a.r || 0)), 8);
    const fresh = pickHouses(full.filter((x) => !best.includes(x)).sort((a, b) => (a.c < b.c ? 1 : -1)), 4);
    const houses = (INDEX.brands || []).slice(0, 12);
    elMain.innerHTML =
      `<div class="flss-ai" hidden></div>` +
      (best.length ? `<div class="flss-h">Customer favorites</div><div class="flss-grid">${best.map((p) => card(p)).join('')}</div>` : '') +
      `<div class="flss-h flss-h--gap">Just landed</div><div class="flss-grid">${fresh.map((p) => card(p)).join('')}</div>` +
      (houses.length ? `<div class="flss-h flss-h--gap">Top brands</div><div class="flss-chips flss-chips--links">${houses.map((b) => { const e = ENTITIES.find((x) => x.kind === 'brand' && x.name === b[0]); return `<a class="flss-chip" href="${esc(e ? e.url : '/collections/vendors?q=' + encodeURIComponent(b[0]))}" data-opt>${esc(display(b[0]))}</a>`; }).join('')}</div>` : '');
  }

  function entityLink(e) {
    const inner = `<span class="flss-ent-k">${e.label}</span><span class="flss-ent-n">${esc(e.kind === 'brand' ? display(e.name) : e.name)}</span><span class="flss-ent-c">${e.count}</span>`;
    const href = e.kind === 'brand' ? e.url : e.handle ? '/collections/' + e.handle : '';
    return href ? `<a class="flss-ent" href="${esc(href)}" data-opt>${inner}</a>` : `<button type="button" class="flss-ent" data-q="${esc(e.name)}" data-opt>${inner}</button>`;
  }

  function render(raw) {
    const res = search(raw);
    const q = res.q;
    elUnderstood.innerHTML = q.chips.length ? `<span class="flss-u-label">Searching for</span>${q.chips.map((c) => `<span class="flss-u">${esc(c)}</span>`).join('')}` : '';
    const all = searchHref(raw);
    elSide.innerHTML =
      (res.entities.length ? `<div class="flss-h">Shop by</div><div class="flss-ents">${res.entities.map(entityLink).join('')}</div>` : '') +
      `<div class="flss-h${res.entities.length ? ' flss-h--gap' : ''}">Narrow it down</div><div class="flss-chips">${refinements(q).map((r) => chip(r.label, r.query)).join('')}</div>`;
    const corrected = res.notCarried
      ? `<p class="flss-meant">We don’t carry <strong>${esc(res.notCarried)}</strong> right now. ${CFG.ai && !aiDown ? 'Below: the closest fragrances from the brands we do carry.' : 'Here is what matches the rest of your search.'}</p>`
      : res.fuzzy.length && res.total && !res.house ? `<p class="flss-meant">Results for <strong>${esc(res.readAs)}</strong> (spelling corrected)</p>` : '';
    const ref = res.reference ? `<p class="flss-meant">Fragrances that share notes with <a href="/products/${esc(res.reference.h)}" data-opt><strong>${esc(res.reference.t)}</strong></a></p>` : '';
    const house = res.house
      ? `<a class="flss-house" href="${esc(res.house.url)}" data-opt><span class="flss-house-k">Brand</span><span class="flss-house-n">${esc(res.house.name)}</span><span class="flss-house-c">View brand · ${plural(res.house.count, 'fragrance')} <span aria-hidden="true">›</span></span></a>`
      : '';
    let body;
    if (res.total) {
      body =
        house +
        `<div class="flss-h">${plural(res.total, 'fragrance')} found${res.total > 8 ? ' <span class="flss-count">best 8 shown</span>' : ''}</div>` +
        corrected + ref +
        `<div class="flss-grid" data-list>${res.products.map((x) => card(x.p, { words: x.words, concepts: q.concepts })).join('')}</div>` +
        `<a class="flss-all" href="${esc(all)}" data-opt>${res.total > 8 ? `View all ${res.total.toLocaleString('en-US')} fragrances` : 'View on the results page'} <span aria-hidden="true">›</span></a>`;
    } else {
      body =
        `<div class="flss-none"><p class="flss-none-t">${res.notCarried ? `We don’t carry ${esc(res.notCarried)} right now.` : `We couldn’t find “${esc(raw.trim())}”.`}</p>` +
        `<p class="flss-none-s">${CFG.ai && !aiDown ? 'Still looking for what you described. Meanwhile, try a brand, a note or a budget: ' : 'Try a brand, a note or a budget: '}` +
        `${['oud', 'vanilla', 'under $75', 'women'].map((x) => chip(x)).join(' ')}</p></div>`;
    }
    elMain.innerHTML = `<div class="flss-ai" hidden></div>` + body;
    announce(res.total ? `${plural(res.total, 'fragrance')} found for ${raw.trim()}.` : `Nothing found for ${raw.trim()}.`);
    askAdvisor(raw, res);
  }

  /** Ways to narrow the request that it does not already say. */
  function refinements(q) {
    const base = q.raw.trim();
    const out = [];
    const add = (label, extra) => out.push({ label, query: `${base} ${extra}`.trim() });
    if (!q.gender) { add('Men', 'for men'); add('Women', 'for women'); }
    if (!q.max && !q.min) { add('Under $75', 'under $75'); add('Under $150', 'under $150'); }
    if (q.season < 0) { add('Summer', 'for summer'); add('Winter', 'for winter'); }
    if (!q.inStock) add('In stock', 'in stock');
    if (!q.sale) add('On sale', 'on sale');
    return out.slice(0, 8);
  }

  /* ------------------------------------------------------------------ behaviour */
  let lastTrigger = null, paintTimer = 0, afterClose = null;
  const options = () => [...elMain.querySelectorAll('[data-opt], [data-q]'), ...elSide.querySelectorAll('[data-opt], [data-q]')].filter((el) => el.offsetParent !== null);

  /** Arrow keys walk from the field through every result and suggestion, and back. */
  function move(delta) {
    const els = [input, ...options()];
    let i = els.indexOf(document.activeElement);
    if (i < 0) i = 0;
    const next = els[(i + delta + els.length) % els.length];
    next.focus();
    if (next !== input) next.scrollIntoView({ block: 'nearest' });
  }

  function update() {
    const raw = input.value;
    elClear.hidden = !raw;
    if (!raw.trim()) { clearTimeout(aiTimer); if (aiAbort) aiAbort.abort(); aiFor = ''; return renderHome(); }
    if (!INDEX) return;
    render(raw);
  }

  function setQuery(q) {
    input.value = q;
    input.focus();
    update();
  }

  function open(trigger, initial) {
    refreshIfStale();
    lastTrigger = trigger || document.activeElement;
    input.placeholder = window.innerWidth < 750 ? 'Brand, perfume or scent…' : 'What are you looking for? A brand, a perfume, a note, or try “fresh for summer”';
    if (typeof initial === 'string' && initial.trim()) input.value = initial;
    if (!dialog.open) dialog.showModal();
    document.documentElement.classList.add('flss-open');
    update();
    requestAnimationFrame(() => { input.focus(); if (!initial) input.select(); });
    load().then(update).catch(() => {
      elMain.innerHTML = `<div class="flss-none"><p class="flss-none-t">Search is taking a moment.</p><p class="flss-none-s">Press Enter to see every result for what you typed.</p></div>`;
    });
    if (CFG.ai && !open.warmed) { open.warmed = true; fetch(CFG.ai, { method: 'POST', headers: { 'Content-Type': 'text/plain' }, body: JSON.stringify({ warm: 1 }) }).catch(() => {}); }
  }

  function close() { if (dialog.open) dialog.close(); }

  dialog.addEventListener('close', () => {
    document.documentElement.classList.remove('flss-open');
    // focus goes back to what opened the panel (the header box too: focusing it no longer re-opens the panel)
    const back = afterClose || lastTrigger; afterClose = null;
    if (back && back.focus && document.contains(back)) {
      back.focus({ preventScroll: true });
      // other scripts' Escape handlers (Globo's search box) can blur it on key-up: put focus back once more
      [150, 400, 800].forEach((ms) => setTimeout(() => { if (!dialog.open && (document.activeElement === document.body || !document.activeElement)) back.focus({ preventScroll: true }); }, ms));
    }
  });
  dialog.addEventListener('click', (e) => {
    if (e.target === dialog) return close();
    if (e.target.closest('.flss-forget')) { forget(); return renderHome(); }
    const q = e.target.closest('[data-q]');
    if (q) { e.preventDefault(); return setQuery(q.getAttribute('data-q')); }
    if (e.target.closest('.flss-close')) return close();
    if (e.target.closest('.flss-clear')) return setQuery('');
    const link = e.target.closest('a[href]');
    if (link && input.value.trim()) remember(input.value);
  });
  input.addEventListener('input', () => {
    clearTimeout(paintTimer);
    paintTimer = setTimeout(update, 60);
  });
  dialog.querySelector('form').addEventListener('submit', (e) => {
    if (!input.value.trim()) { e.preventDefault(); return; }
    remember(input.value);
  });
  dialog.addEventListener('keydown', (e) => {
    const onField = document.activeElement === input;
    // a11y 2026-10-27: Escape always closes the panel (a search field with text would otherwise only clear itself)
    if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); close(); return; }
    // a11y 2026-10-27: Tab past the last item (or Shift+Tab before the first) closes the panel and carries on in the page
    if (e.key === 'Tab') {
      const f = [...dialog.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), [tabindex]:not([tabindex="-1"])')].filter((el) => el.offsetParent !== null || el === input);
      const first = f[0], last = f[f.length - 1];
      if ((!e.shiftKey && document.activeElement === last) || (e.shiftKey && document.activeElement === first)) {
        e.preventDefault();
        const t = lastTrigger;
        let dest = t;
        if (!e.shiftKey && t && document.contains(t)) {
          const all = [...document.querySelectorAll('a[href], button:not([disabled]), input:not([type="hidden"]):not([disabled]), select, textarea, summary, [tabindex]:not([tabindex="-1"])')]
            .filter((el) => !dialog.contains(el) && el.tabIndex >= 0 && el.getClientRects().length && getComputedStyle(el).visibility !== 'hidden');
          const scope = t.closest('form, details') || t;
          let i = all.indexOf(t);
          while (i > -1 && i + 1 < all.length && scope.contains(all[i + 1])) i++;   // skip the rest of the search box's own controls
          if (i > -1 && all[i + 1]) dest = all[i + 1];
        }
        afterClose = dest;
        close();
        return;
      }
    }
    if (e.key === 'ArrowDown') { e.preventDefault(); move(1); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); move(-1); }
    else if (!onField && !e.metaKey && !e.ctrlKey && !e.altKey && ((e.key.length === 1 && e.key !== ' ') || e.key === 'Backspace')) input.focus();
  });

  /* Take over the header search: every search box in the header group (desktop header-single-line, the phone's
     sticky header-mobile, the menu drawer) and its search button, the phone search icon, any element with
     data-fl-search-open, and "/" or Cmd/Ctrl+K. The results page's own search form is left alone. */
  const HEADER = '.shopify-section-group-header-group, header, sticky-header-mobile, .header-mobile, #Search-In-Modal-Sidebar';
  const BOX = HEADER.split(', ').map((h) => (h.startsWith('#') ? h : `${h} input[name="q"]`)).join(', ');
  const TRIGGER = `${BOX}, .shopify-section-group-header-group form[role="search"] button, .header__search summary, summary[data-search-mobile], .header__icon--search, [data-fl-search-open]`;
  const trigger = (e) => {
    const t = e.target instanceof Element ? e.target.closest(TRIGGER) : null;
    if (!t || dialog.contains(t) || t.closest('#fl-search-page')) return;
    e.preventDefault();
    e.stopImmediatePropagation();
    if (e.type === 'pointerdown') return; // the click that follows opens it (keeps phones from focusing the header box)
    const box = t.matches('input') ? t : t.closest('form') && t.closest('form').querySelector('input[name="q"]');
    if (box) box.blur();
    open(t, box ? box.value : '');
  };
  window.addEventListener('pointerdown', trigger, true);
  window.addEventListener('click', trigger, true);
  // a11y 2026-10-27: the panel no longer opens just because the header box gets focus (Tab used to be trapped in it and
  // never reached the menu). It opens on click / Enter / Space on the search control, or as soon as you type in the box.
  // Focus alone still belongs to this panel: other scripts' search pop-ups (Globo's) must not open on focus either.
  const keepFocus = (e) => { if (e.target instanceof Element && e.target.matches(BOX) && !dialog.contains(e.target) && !dialog.open) e.stopImmediatePropagation(); };
  window.addEventListener('focusin', keepFocus, true);
  window.addEventListener('focus', keepFocus, true);
  window.addEventListener('input', (e) => {
    const b = e.target;
    if (!(b instanceof Element) || !b.matches(BOX) || dialog.contains(b) || dialog.open) return;
    e.stopImmediatePropagation();   // the old small dropdown (fl-search.js) must not open behind the panel
    const v = b.value;
    b.blur();
    open(b, v);
    requestAnimationFrame(() => { try { const n = input.value.length; input.setSelectionRange(n, n); } catch (err) {} });
  }, true);
  window.addEventListener('keydown', (e) => {
    const typing = /^(input|textarea|select)$/i.test((e.target && e.target.tagName) || '') || (e.target && e.target.isContentEditable);
    if ((e.key === 'k' || e.key === 'K') && (e.metaKey || e.ctrlKey)) { e.preventDefault(); dialog.open ? close() : open(); }
    else if (e.key === '/' && !typing && !dialog.open) { e.preventDefault(); open(); }
  });
  // Have the index ready before the first click: when the browser is idle, or when the pointer nears the search.
  const warm = () => load().catch(() => {});
  document.addEventListener('pointerover', function near(e) {
    if (e.target instanceof Element && e.target.closest(HEADER.split(', ').slice(0, 4).map((h) => h + ' form[role="search"]').join(', ') + ', summary[data-search-mobile], [data-fl-search-open]')) { warm(); document.removeEventListener('pointerover', near); }
  });
  const saver = navigator.connection && navigator.connection.saveData;
  if (!saver) {
    const later = () => ('requestIdleCallback' in window ? requestIdleCallback(warm, { timeout: 8000 }) : setTimeout(warm, 4000));
    if (document.readyState === 'complete') later(); else window.addEventListener('load', later, { once: true });
  }

  window.FLSearch = {
    open, close, parse, load,
    search: (q) => load().then(() => search(q)),
    searchNow: search,
    wants: wantsAdvisor,
    index: () => INDEX,
    cfg: CFG,
    remember,
  };
  dialog.setAttribute('data-ready', '1');
  document.dispatchEvent(new CustomEvent('fl-search-ready'));
})();

/* Picture safety net: when a product picture fails to load, ask the shop for that product's current picture; if that
   fails too, show a plain tile, never a broken icon. */
(function () {
  function tile(im) {
    var s = document.createElement('span');
    s.className = 'flss-noimg';
    s.setAttribute('aria-hidden', 'true');
    if (im.parentNode) im.parentNode.replaceChild(s, im);
  }
  document.addEventListener('error', function (e) {
    var im = e.target;
    if (!im || im.tagName !== 'IMG' || !im.dataset || !im.dataset.flssH) return;
    if (im.dataset.flssTried) { tile(im); return; }
    im.dataset.flssTried = '1';
    fetch('/products/' + encodeURIComponent(im.dataset.flssH) + '.js', { credentials: 'same-origin' })
      .then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) {
        var u = p && (p.featured_image || (p.images && p.images[0]));
        if (!u) { tile(im); return; }
        u = String(u).split('?')[0]; if (u.indexOf('//') === 0) u = 'https:' + u;
        im.src = u + '?width=' + (parseInt(im.getAttribute('width') || '84', 10) * 2);
      })
      .catch(function () { tile(im); });
  }, true);
})();
